import { defineStore, acceptHMRUpdate } from 'pinia';
import { i18n_t as $t } from '@/utils/i18n';
import { useOrganizationStore } from '@/stores/organization.store';
import keyBy from 'lodash/keyBy';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import {
    createEmailDomain,
    createOmnichannel,
    deleteEmailDomain,
    deleteOmniChannelById,
    getEmailProfilesForKind,
    loadEmail,
    loadEmailIdentities,
    loadEmailIdentity,
    loadEmailProviders,
    loadEmails,
    loadOrganizationEmailIdentities,
    loadOrganizationEmailIdentity,
    replaceDefaultSubject,
    saveEmailProfile,
    updateEmailField,
    updateEmailFieldObj,
    updateEmailMailFromDomain,
    updateOmniChannelField,
    cloneOmnichannel,
} from '@/emails/emails.utils';
import { LOADING_STATES } from '@/constants';
import { EMAIL_STATES, EMAIL_CHANNEL, EMAIL_CONTENT_SOURCE } from '@/emails/constants';
import { buildClonedEmailGuide, getClonedEmailDisplayName, isHtmlEmail as isHtmlContentSource, isIntegratedEmail, isScheduled } from '@/emails/helpers';
import { PROVIDER_TYPES } from '@/orchestrate/journeys/integrations.constants';
import { DOMAIN_STATUSES } from '@/settings/subscription-settings/domains-settings/utils/constants';
import { BuildingBlockEmails } from '@pendo/services/BuildingBlocks';
import { EMAIL } from '@pendo/services/Constants';
import { areEmailSettingsValid as checkEmailSettingsValid, getEmailType } from '@pendo/services/EmailUtils';
import { canUpdateEmail } from '@/emails/emails-permissions.utils';

import { useProductLinesStore } from '@/stores/product-lines.store';
import { useSubscriptionStore } from '@/stores/subscription.store';

import { useAuthStore } from '@/stores/auth.store';

function getGuidesStore () {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    return require('@/stores/guides.store').useGuidesStore();
}

const { EMAIL_TYPES } = EMAIL;
const { LOADING, RESOLVED, REJECTED, EMPTY } = LOADING_STATES;
const { AWS } = PROVIDER_TYPES;
const { VERIFIED, UNVERIFIED, PENDING } = DOMAIN_STATUSES;
const { BLOCKS } = EMAIL_CONTENT_SOURCE;

export const EMPTY_VALUE = '__EMPTY__';

export const FIELD_TYPES = {
    EMAIL_SETTINGS: 'emailsettings',
    SUBJECT: 'subject',
};

export const emailObj = {
    name: undefined,
    appId: undefined,
    audience: [
        {
            source: {
                visitors: null,
            },
        },
        {
            filter: 'false',
        },
    ],
    audienceUiHint: {
        filters: [
            {
                kind: 'visitor',
                segmentId: 'noOne',
            },
        ],
    },
    channelType: undefined,
    subject: undefined,
    emailSettings: {
        friendlyFromName: undefined,
        from: undefined,
        identity: undefined,
        recipientMetadataPropertyName: undefined,
        replyTo: undefined,
        type: undefined,
    },
};

interface EmailSettings {
    friendlyFromName?: string;
    from?: string;
    identity?: string;
    recipientMetadataPropertyName?: string;
    replyTo?: string;
    type?: string;
}

function normalizeEmailSettings (settings: Record<string, unknown> | EmailSettings): EmailSettings {
    const normalized = { ...settings } as EmailSettings;

    if (normalized.replyTo === '') {
        normalized.replyTo = normalized.from;
    }

    return normalized;
}

interface Email {
    id: string;
    name?: string;
    appId?: string;
    guideId?: string;
    channelType?: string;
    subject?: string;
    status?: string;
    emailType?: string;
    contentSource?: string;
    emailSettings?: EmailSettings;
    [key: string]: unknown;
}

interface Domain {
    identity: string;
    type?: string;
    isOrganization?: boolean;
    identityVerified?: boolean;
    mailFromDomainVerified?: boolean;
    sendingEnabled?: boolean;
    dkimVerificationStatus?: string;
    mailFromDomainStatus?: string;
}

interface EmailProfile {
    id?: string;
    default?: boolean;
    [key: string]: unknown;
}

interface EmailsState {
    map: Record<string, Email>;
    integration: Record<string, unknown[]>;
    integrationError: Record<string, unknown>;
    activeEmailId: string | null;
    domains: Domain[] | null;
    editing: boolean;
    saving: boolean;
    getAllEmailsStatus: string;
    profiles: EmailProfile[];
    promises: {
        loadProfiles: Promise<void> | null;
    };
    identitiesStatus: string | null;
}

function getInitialState (): EmailsState {
    return {
        map: {},
        integration: {},
        integrationError: {},
        activeEmailId: null,
        domains: null,
        editing: false,
        saving: false,
        getAllEmailsStatus: LOADING,
        profiles: [],
        promises: {
            loadProfiles: null,
        },
        identitiesStatus: null,
    };
}

export const useEmailsStore = defineStore('emails', {
    state: getInitialState,
    getters: {
        getSaving: (state) => state.saving,
        list: (state) => Object.values(state.map),
        getValidDomainIdentities (): string[] {
            return this.getDomains?.map((domain: Domain) => domain.identity) || [];
        },
        getEmailById () {
            return (id: string): Email | null => {
                const email = this.map[id];
                if (!email) return null;

                const emailIdentity = email.emailSettings?.identity;

                if (emailIdentity && !this.getValidDomainIdentities.includes(emailIdentity)) {
                    return {
                        ...email,
                        emailSettings: {
                            ...email.emailSettings,
                            identity: '',
                        },
                    };
                }

                return email;
            };
        },
        getEmailTypeById () {
            return (id: string) => this.getEmailById(id)?.emailType || EMAIL_TYPES.MARKETING;
        },
        getSubjectLine (): string | undefined {
            return replaceDefaultSubject(this.getActiveEmail)?.subject;
        },
        getActiveEmail (): Email | null {
            return this.getEmailById(this.activeEmailId as string);
        },
        getEmailSettings (): EmailSettings {
            return this.getActiveEmail?.emailSettings || {};
        },
        getActiveEmailAnalytics: (state) => (state as unknown as Record<string, unknown>).activeEmailAnalytics,
        getActiveEmailAnalyticsStatus (): unknown {
            return (this.getActiveEmailAnalytics as Record<string, unknown>)?.status;
        },
        isEmailLaunched (): boolean {
            return (
                [EMAIL_STATES.SENT, EMAIL_STATES.DISABLED].includes(this.getActiveEmail?.status as string) ||
                (this.getActiveEmail?.status === EMAIL_STATES.PUBLIC && !this.isEmailScheduled)
            );
        },
        isEmailScheduled (): boolean {
            return isScheduled(this.getActiveEmail);
        },
        isEmailSettingsEmpty (): boolean {
            return !this.getEmailSettings || !this.getEmailSettings.identity;
        },
        areEmailSettingsValid (): boolean {
            return checkEmailSettingsValid({
                ...this.getEmailSettings,
                subject: this.getSubjectLine,
            });
        },
        isEditing: (state) => state.editing,
        isEmailEditingBlocked (): boolean {
            return (
                !canUpdateEmail({ appId: this.getActiveEmail?.appId, source: undefined, target: undefined, email: this.getActiveEmail }) ||
                [EMAIL_STATES.SENT, EMAIL_STATES.PUBLIC, EMAIL_STATES.DISABLED].includes(
                    this.getActiveEmail?.status as string,
                )
            );
        },
        isEmailIntegrated (): boolean {
            return !!(this.getActiveEmail && isIntegratedEmail(this.getActiveEmail as unknown as { provider: string }));
        },
        hasEmailStandAlone (): boolean {
            return useProductLinesStore().hasProductLinePurchased('orchestrate', 'emailStandAloneEnabled');
        },
        getDomains: (state): Domain[] => state.domains || [],
        excludeMailFromVerification (): boolean {
            return useAuthStore().hasSegmentFlag('excludeMailFromVerification');
        },
        getVerifiedDomains (): Domain[] {
            return this.getDomains?.filter(({ identityVerified = false, mailFromDomainVerified = false }) => {
                if (this.excludeMailFromVerification) {
                    return identityVerified;
                }

                return identityVerified && mailFromDomainVerified;
            });
        },
        getVerifiedDomainIdentities (): string[] {
            return this.getVerifiedDomains?.map(({ identity }: Domain) => identity) || [];
        },
        getSendingDisabled (): Domain[] {
            return this.getVerifiedDomains?.filter(({ sendingEnabled = false }) => !sendingEnabled) || [];
        },
        getVerifiedDomainForSending (): string[] {
            return (
                this.getVerifiedDomains
                    ?.filter(({ sendingEnabled = false }) => sendingEnabled)
                    .map(({ identity }: Domain) => identity) || []
            );
        },
        domainVerificationStatus (): (domain?: Partial<Domain>) => string {
            return (domain: Partial<Domain> = {}) => {
                if (this.getVerifiedDomainIdentities.includes(domain.identity as string)) return VERIFIED;

                const { dkimVerificationStatus, mailFromDomainStatus } = domain;

                if (this.excludeMailFromVerification) {
                    return (
                        ({
                            FAILED: UNVERIFIED,
                            PENDING,
                        } as Record<string, string>)[dkimVerificationStatus as string] || PENDING
                    );
                }

                if ([dkimVerificationStatus, mailFromDomainStatus].includes('FAILED')) {
                    return UNVERIFIED;
                }

                return PENDING;
            };
        },
        getProfileById: (state) => (id: string) => {
            return state.profiles.find((profile) => profile.id === id);
        },
        defaultProfile: (state) => {
            return state.profiles.find((profile) => profile.default);
        },
        getEmailQuotaSize (): number {
            return useAuthStore().hasSegmentFlag('allow250kEmailQuota') ? 250000 : 60000;
        },
        getAssociatedGuide (): unknown {
            const activeEmail = this.getActiveEmail;
            if (!activeEmail) return null;

            const guide = getGuidesStore().getGuideById(activeEmail.guideId);
            if (!guide) return null;

            return guide;
        },
        getBuildingBlocks (): unknown {
            const bbString = get(this.getAssociatedGuide as Record<string, unknown>, 'steps.0.buildingBlocks');
            if (!bbString) return null;

            return JSON.parse(bbString as string);
        },
        isLocalizationEnabled (): boolean {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');
            const hasApplicationFlag = useAppsStore().getApplicationFlag(
                (this.getAssociatedGuide as Record<string, unknown>)?.appId,
                'guideLocalization',
            );
            const hasProductLine = useProductLinesStore().hasProductLinePurchased(
                'orchestrate',
                'emailLocalization',
            );
            const hasSegmentFlag = useAuthStore().hasSegmentFlag('emailLocalization');

            return hasSegmentFlag || (hasApplicationFlag && hasProductLine);
        },
        getIdentitiesStatus: (state) => state.identitiesStatus,
        hasEmailThrottlingPermission (): boolean {
            const isEmailThrottleSettingsEnabled =
                useSubscriptionStore().getSubscriptionEmailThrottleSettings.enabled;
            const hasSegmentFlag = useAuthStore().hasSegmentFlag('emailThrottling');

            return hasSegmentFlag && Boolean(isEmailThrottleSettingsEnabled);
        },
        getEmailsTabs (): unknown[] {
            const hasSegmentFlag = useAuthStore().hasSegmentFlag('emailThrottling');
            const tabs = [
                { prop: 'overview', label: $t('emails.Overview'), lazy: true },
                { prop: 'metrics', label: $t('emails.Metrics'), lazy: true },
            ];

            if (hasSegmentFlag) {
                tabs.push({ prop: 'settings', label: $t('emails.Settings'), lazy: true });
            }

            return tabs;
        },
        isMarketingEmail () {
            return (id: string) => {
                return this.getEmailTypeById(id) === EMAIL_TYPES.MARKETING;
            };
        },
        isHtmlEmail (): boolean {
            return isHtmlContentSource(this.getActiveEmail);
        },
        isHtmlEmailContentEmpty (): boolean {
            if (!this.isHtmlEmail || !this.getActiveEmail) return false;

            return !this.getActiveEmail.htmlTemplateUrl;
        },
    },
    actions: {
        addOrUpdateEmail ({ email }: { email: Email }) {
            this.map = {
                ...this.map,
                [email.id]: replaceDefaultSubject(email),
            };
        },
        setActiveEmailById ({ id }: { id: string | null }) {
            this.activeEmailId = id;
            this.editing = false;
            this.saving = false;
        },
        setDomains ({ domains }: { domains: Domain[] }) {
            this.domains = domains;
        },
        addDomain ({ domain }: { domain: Domain }) {
            if (!this.domains) this.domains = [];

            this.domains.push(domain);
        },
        removeDomain ({ identity }: { identity: string }) {
            this.domains = (this.domains || []).filter((domain) => domain.identity !== identity);
        },
        setMap ({ map }: { map: Record<string, Email> }) {
            this.map = map;
        },
        setIntegration ({ provider, list }: { provider: string; list: unknown[] }) {
            this.integration[provider] = list;
        },
        setIntegrationError ({ provider, error }: { provider: string; error: unknown }) {
            this.integrationError[provider] = error;
        },
        removeEmail ({ id }: { id: string }) {
            delete this.map[id];
        },
        setSaving ({ saving }: { saving: boolean }) {
            this.saving = saving;
        },
        setEditing ({ editing }: { editing: boolean }) {
            this.editing = editing;
        },
        setGetAllEmailsStatus ({ status }: { status: string }) {
            this.getAllEmailsStatus = status;
        },
        setProfiles ({ profiles }: { profiles: EmailProfile[] }) {
            this.profiles = profiles;
        },
        setPromise ({ promise, key }: { promise: Promise<void> | null; key: string }) {
            (this.promises as Record<string, unknown>)[key] = promise;
        },
        setIdentitiesStatus ({ status }: { status: string | null }) {
            this.identitiesStatus = status;
        },
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            const hasOrchestrateProductLine = useProductLinesStore().hasProductLinePurchased('orchestrate');
            if (!hasOrchestrateProductLine || ([RESOLVED, EMPTY].includes(this.getAllEmailsStatus) && !reFetch)) {
                return;
            }

            try {
                this.setGetAllEmailsStatus({ status: LOADING });
                const { data } = await loadEmails();
                const emailsList = data.map((email: Email) => replaceDefaultSubject(email));
                const getAllEmailsStatus = emailsList.length ? RESOLVED : EMPTY;
                this.setMap({ map: keyBy(emailsList, 'id') });
                this.setGetAllEmailsStatus({ status: getAllEmailsStatus });
            } catch {
                this.setGetAllEmailsStatus({ status: REJECTED });
            }
        },
        async loadOne ({ id, setActive = false, reFetch = false }: { id: string; setActive?: boolean; reFetch?: boolean }) {
            const emailInState = this.getEmailById(id);

            if (emailInState && !reFetch) {
                if (setActive) this.setActiveEmail(emailInState);

                return;
            }

            try {
                const { data: email } = await loadEmail(id);
                this.addOrUpdateEmail({ email });

                if (setActive) this.setActiveEmail(email);
            } catch {
                throw new Error($t('emails.Failed to load email'));
            }
        },
        async loadEmailProviders () {
            try {
                const { data: providers } = await loadEmailProviders();

                return providers;
            } catch {
                this.setIntegrationError({
                    provider: 'general',
                    error: new Error($t('emails.Failed to load email providers')),
                });
            }
        },
        async updateEmailField ({ id, field, value }: { id?: string; field: string; value: unknown }) {
            const emailId = id ?? (this.activeEmailId as string);
            const email = this.map[emailId];

            if (!email) return;

            try {
                const { data: updatedEmail } = await updateEmailField(emailId, EMAIL_CHANNEL, field, value);

                this.addOrUpdateEmail({
                    email: updatedEmail,
                });
            } catch {
                throw new Error($t('emails.Failed to update email field'));
            }
        },
        async updateEmailFieldObj ({ id, field, value }: { id?: string; field: string; value: unknown }) {
            const emailId = id ?? (this.activeEmailId as string);
            const email = this.map[emailId];

            if (!email) return;

            try {
                const { data: updatedEmail } = await updateEmailFieldObj(emailId, EMAIL_CHANNEL, field, value);

                this.addOrUpdateEmail({
                    email: { ...email, ...updatedEmail },
                });
            } catch {
                throw new Error($t('emails.Failed to update email field'));
            }
        },
        async cloneOmnichannel ({
            id,
            journeyId,
            name,
        }: {
            id: string;
            journeyId?: string;
            name?: string;
        }) {
            try {
                const { data: clonedEmail } = await cloneOmnichannel(id, { journeyId, name });
                this.addOrUpdateEmail({ email: clonedEmail });

                return clonedEmail;
            } catch {
                throw new Error($t('emails.Failed to clone email'));
            }
        },
        async updateOmniChannelField ({ emailId, field, value }: { emailId?: string; field: string; value: unknown }) {
            this.setSaving({ saving: true });
            const email = this.map[emailId || (this.activeEmailId as string)];

            if (!email) return;

            try {
                const { data: updatedEmail } = await updateOmniChannelField(email.id, field, value);

                this.addOrUpdateEmail({
                    email: updatedEmail,
                });
            } catch {
                throw new Error($t('emails.Unable to update email'));
            } finally {
                this.setSaving({ saving: false });
            }
        },
        async createOmnichannel ({
            name,
            description,
            appId,
            channelType,
            subject = EMPTY_VALUE,
            guideId,
            provider = AWS,
            setActive = false,
            schedule,
            attributes = {},
            emailType,
            contentSource = BLOCKS,
        }: {
            name: string;
            description?: string;
            appId?: string;
            channelType?: string;
            subject?: string;
            guideId?: string;
            provider?: string;
            setActive?: boolean;
            schedule?: unknown;
            attributes?: Record<string, unknown>;
            emailType?: string;
            contentSource?: string;
        }) {
            try {
                const { data: email } = await createOmnichannel({
                    ...emailObj,
                    name,
                    description,
                    appId,
                    channelType,
                    subject,
                    ...(guideId !== undefined ? { guideId } : {}),
                    provider,
                    schedule,
                    attributes,
                    emailType,
                    contentSource,
                });

                this.addOrUpdateEmail({ email });

                if (setActive) this.setActiveEmailById({ id: email.id });

                return email;
            } catch {
                throw new Error($t('emails.Failed to create omnichannel'));
            }
        },
        async createEmailDomain ({ domain, mailFrom }: { domain: string; mailFrom: string }) {
            try {
                const { data: createdDomain } = await createEmailDomain(domain);

                await updateEmailMailFromDomain(domain, mailFrom);

                const activeSub = useSubscriptionStore().getActiveSub;
                const emailSettings = activeSub.emailSettings || {};
                await useSubscriptionStore().updateProperty({
                    property: 'emailSettings',
                    value: {
                        ...(emailSettings || {}),
                        emailIdentities: {
                            ...(emailSettings.emailIdentities || {}),
                            [createdDomain.identity]: {
                                type: createdDomain.type,
                                identity: createdDomain.identity,
                                mailFromDomain: mailFrom,
                            },
                        },
                    },
                });

                await this.loadDomainIdentities({});
                await this.loadDomainRecords();

                return createdDomain;
            } catch (ex) {
                throw new Error($t('emails.Create email domain was unsuccessful', { error: ex }));
            }
        },
        async deleteEmailDomain ({ identity }: { identity: string }) {
            try {
                await deleteEmailDomain(identity);

                const activeSub = useSubscriptionStore().getActiveSub;
                const emailSettings = activeSub.emailSettings || {};
                const emailIdentities = { ...(emailSettings.emailIdentities || {}) };
                delete emailIdentities[identity];

                await useSubscriptionStore().updateProperty({
                    property: 'emailSettings',
                    value: {
                        ...(emailSettings || {}),
                        emailIdentities,
                    },
                });

                this.removeDomain({ identity });
            } catch (ex) {
                throw new Error($t('emails.Delete email domain was unsuccessful', { error: ex }));
            }
        },
        setActiveEmail ({ id, guideId }: { id: string; guideId?: string }) {
            this.setActiveEmailById({ id });

            if (guideId) {
                getGuidesStore().loadOne({ setActive: true, id: guideId });
            } else {
                getGuidesStore().setActiveGuide({ guide: null });
            }
        },
        resetActiveEmail () {
            this.setActiveEmailById({ id: null });
            getGuidesStore().setActiveGuide({ guide: null });
        },
        async deleteEmail ({ id }: { id: string }) {
            try {
                await deleteOmniChannelById(id);

                this.removeEmail({ id });
            } catch {
                throw new Error($t('emails.Failed to delete email'));
            }
        },
        async updateEmailSettings ({ settings }: { settings: Record<string, unknown> }) {
            const { subjectLine, previewText, ...detailsSettings } = settings as {
                subjectLine?: string;
                previewText?: string;
                replyTo?: string;
                from?: string;
                [key: string]: unknown;
            };

            try {
                const emailSettingsToUpdate = normalizeEmailSettings(detailsSettings);
                const currentEmailSettings = normalizeEmailSettings(this.getEmailSettings);

                if (!isEqual(emailSettingsToUpdate, currentEmailSettings)) {
                    await this.updateEmailFieldObj({
                        field: FIELD_TYPES.EMAIL_SETTINGS,
                        value: emailSettingsToUpdate,
                    });
                }

                if (subjectLine !== this.getSubjectLine) {
                    await this.updateEmailField({
                        field: FIELD_TYPES.SUBJECT,
                        value: subjectLine,
                    });
                }

                if ((!!previewText || previewText === '') && this.getAssociatedGuide) {
                    const clonedGuide = cloneDeep(this.getAssociatedGuide) as Record<string, unknown>;
                    const steps = clonedGuide.steps as Array<Record<string, unknown>>;
                    const raw = steps[0].buildingBlocks;

                    steps[0].buildingBlocks = typeof raw === 'string' ? JSON.parse(raw) : raw;
                    steps[0].buildingBlocks = BuildingBlockEmails.setPreviewTextOnBuildingBlocks(
                        steps[0].buildingBlocks,
                        previewText,
                    );

                    await getGuidesStore().saveGuide({ guide: clonedGuide });
                }
            } catch {
                throw new Error($t('emails.Failed to update email settings'));
            }
        },
        async cloneGuideByEmailId ({ id }: { id: string }) {
            const email = this.getEmailById(id);
            if (!email) return;

            const { guideId } = email;
            const guide = getGuidesStore().getGuideById(guideId);
            if (!guide) return;

            try {
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const { useAppsStore } = require('@/stores/apps.store');
                const app = useAppsStore().getAppById((guide as Record<string, unknown>).appId);
                const clonedEmailGuide = await buildClonedEmailGuide(guide, app);

                return await getGuidesStore().saveGuide({ guide: clonedEmailGuide });
            } catch {
                throw new Error($t('emails.Failed to clone guide by email id'));
            }
        },
        async cloneEmail ({ id }: { id: string }) {
            const email = this.getEmailById(id);
            const clonedGuide = await this.cloneGuideByEmailId({ id });

            if (!clonedGuide) return;

            try {
                const clonedEmail = await this.createOmnichannel({
                    name: getClonedEmailDisplayName(email?.name),
                    appId: (clonedGuide as Record<string, unknown>).appId as string,
                    channelType: EMAIL_CHANNEL,
                    guideId: (clonedGuide as Record<string, unknown>).id as string,
                    emailType: getEmailType(clonedGuide),
                });

                return clonedEmail;
            } catch {
                throw new Error($t('emails.Failed to clone email'));
            }
        },
        async updateEmailName ({ name }: { name: string }) {
            const email = this.activeEmailId && this.map[this.activeEmailId];

            if (!email) return;

            await this.updateOmniChannelField({ field: 'name', value: name });
        },
        async loadDomainIdentities ({ appId }: { appId?: string } = {}) {
            const orgId = useOrganizationStore().getActive?.id;

            const promises = [
                loadEmailIdentities(appId),
                ...(orgId ? [loadOrganizationEmailIdentities(orgId)] : []),
            ];

            const results = await Promise.all(promises);
            const domains = results.reduce((acc: Domain[], { data = [] }) => [...acc, ...data], []);

            this.setDomains({ domains });
        },
        async loadDomainRecords () {
            if (!this.domains) return;

            const orgId = useOrganizationStore().getActive?.id;

            const loadDomainRecord = async ({ identity, isOrganization }: Domain) => {
                const { data } =
                    isOrganization && orgId
                        ? await loadOrganizationEmailIdentity(orgId, identity)
                        : await loadEmailIdentity(identity);

                return data;
            };

            const domains = await Promise.all(
                (this.domains as Domain[]).filter((domain) => domain?.type === 'DOMAIN').map(loadDomainRecord),
            );

            this.setDomains({ domains });
        },
        async loadDomainIdentitiesAndRecords ({ appId }: { appId?: string } = {}) {
            this.setIdentitiesStatus({ status: LOADING });

            try {
                await this.loadDomainIdentities({ appId });
                await this.loadDomainRecords();

                if ((this.domains as Domain[]).length === 0) {
                    this.setIdentitiesStatus({ status: EMPTY });
                } else {
                    this.setIdentitiesStatus({ status: RESOLVED });
                }
            } catch {
                this.setIdentitiesStatus({ status: REJECTED });
            }
        },
        loadProfiles ({ reFetch = false, kind }: { reFetch?: boolean; kind?: string } = {}) {
            if (this.promises.loadProfiles) {
                return this.promises.loadProfiles;
            }

            if (!isEmpty(this.profiles) && !reFetch) {
                return Promise.resolve();
            }

            const promise = getEmailProfilesForKind(kind)
                .then((profiles: EmailProfile[]) => {
                    this.setProfiles({ profiles });
                })
                .catch((error: unknown) => {
                    throw error;
                })
                .finally(() => {
                    this.setPromise({ key: 'loadProfiles', promise: null });
                });

            this.setPromise({ key: 'loadProfiles', promise });

            return promise;
        },
        async saveProfile ({
            friendlyFromName,
            identity,
            from,
            replyTo,
            recipientMetadataPropertyName,
            isDefault,
            kind,
            type,
        }: {
            friendlyFromName?: string;
            identity?: string;
            from?: string;
            replyTo?: string;
            recipientMetadataPropertyName?: string;
            isDefault?: boolean;
            kind?: string;
            type?: string;
        }) {
            try {
                await saveEmailProfile({
                    friendlyFromName,
                    identity,
                    from,
                    replyTo,
                    recipientMetadataPropertyName: recipientMetadataPropertyName || 'agent.email',
                    type: type || 'DOMAIN',
                    isDefault,
                    kind,
                });
                await this.loadProfiles({ reFetch: true, kind });
            } catch (error) {
                throw error;
            }
        },
        async loadEmailThrottlingPermission () {
            const settings = useSubscriptionStore().getSubscriptionEmailThrottleSettings;
            if (!settings || !Object.keys(settings).length) {
                await useSubscriptionStore().ensureSubscriptionEmailThrottleSettingsLoaded();
            }

            return this.hasEmailThrottlingPermission;
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useEmailsStore, module.hot));
}
